import React, { useState, useMemo } from 'react';
import { FileSpreadsheet, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import * as XLSX from 'xlsx';

const DailySalesReport = ({ deliveries = [], collections = [], customers = [] }) => {
  const today = new Date().toISOString().split('T')[0];
  const [reportDate, setReportDate] = useState(today);

  const prevDay = () => {
    const d = new Date(reportDate);
    d.setDate(d.getDate() - 1);
    setReportDate(d.toISOString().split('T')[0]);
  };
  const nextDay = () => {
    const d = new Date(reportDate);
    d.setDate(d.getDate() + 1);
    setReportDate(d.toISOString().split('T')[0]);
  };

  const dayDeliveries  = useMemo(() => deliveries.filter(d => d.date === reportDate), [deliveries, reportDate]);
  const dayCollections = useMemo(() => collections.filter(c => c.date === reportDate), [collections, reportDate]);

  const totalSalesAmt    = dayDeliveries.reduce((s, d) => s + (d.amount || 0), 0);
  const totalSalesQty    = dayDeliveries.reduce((s, d) => s + (d.quantity || 0), 0);
  const totalPurchaseAmt = dayCollections.reduce((s, c) => s + (c.amount || 0), 0);
  const totalPurchaseQty = dayCollections.reduce((s, c) => s + (c.quantity || 0), 0);

  const displayDate = (() => {
    const d = new Date(reportDate + 'T00:00:00');
    const days = ['일', '월', '화', '수', '목', '금', '토'];
    return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 (${days[d.getDay()]})`;
  })();

  const itemDesc = (d) => {
    if (d.items && typeof d.items === 'object') {
      return Object.entries(d.items).map(([k, v]) => `${k} ${v}본`).join(', ');
    }
    return `${d.item || ''} ${d.quantity ? d.quantity + '본' : ''}`.trim();
  };

  const downloadExcel = () => {
    const wb = XLSX.utils.book_new();

    // ── 판매(배송) 시트 ───────────────────────────────────────
    const salesHeader = [['번호', '거래처명', '품목', '수량(본)', '금액(원)', '비고']];
    const salesRows = dayDeliveries.map((d, i) => [
      i + 1,
      d.customer,
      itemDesc(d),
      d.quantity || 0,
      d.amount || 0,
      d.memo || '',
    ]);
    const salesSummary = [
      [],
      ['', '합  계', '', totalSalesQty, totalSalesAmt, ''],
    ];

    const salesData = [
      [`일일 판매일보 — ${displayDate}`],
      [],
      ...salesHeader,
      ...salesRows,
      ...salesSummary,
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(salesData);

    // 열 너비
    ws1['!cols'] = [{ wch: 6 }, { wch: 18 }, { wch: 30 }, { wch: 10 }, { wch: 14 }, { wch: 20 }];

    // 제목 병합
    ws1['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];

    // 스타일 (xlsx community edition은 스타일 미지원이지만 구조는 남겨둠)
    XLSX.utils.book_append_sheet(wb, ws1, '판매일보');

    // ── 매입(회수) 시트 ───────────────────────────────────────
    const purchaseHeader = [['번호', '거래처명', '품목', '수량(본)', '금액(원)', '비고']];
    const purchaseRows = dayCollections.map((c, i) => [
      i + 1,
      c.customer,
      itemDesc(c),
      c.quantity || 0,
      c.amount || 0,
      c.memo || '',
    ]);
    const purchaseSummary = [
      [],
      ['', '합  계', '', totalPurchaseQty, totalPurchaseAmt, ''],
    ];

    const purchaseData = [
      [`일일 매입일보 — ${displayDate}`],
      [],
      ...purchaseHeader,
      ...purchaseRows,
      ...purchaseSummary,
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(purchaseData);
    ws2['!cols'] = [{ wch: 6 }, { wch: 18 }, { wch: 30 }, { wch: 10 }, { wch: 14 }, { wch: 20 }];
    ws2['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];
    XLSX.utils.book_append_sheet(wb, ws2, '매입일보');

    // 파일명: 대성에너지_판매일보_2025-06-01.xlsx
    const filename = `대성에너지_판매일보_${reportDate}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #0c2d5e 0%, #185FA5 100%)',
      borderRadius: 18,
      padding: '22px 28px',
      display: 'flex',
      alignItems: 'center',
      gap: 24,
      flexWrap: 'wrap',
      boxShadow: '0 6px 24px rgba(24,95,165,0.25)',
    }}>
      {/* 아이콘 + 제목 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: '1 1 200px' }}>
        <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 14, padding: 13 }}>
          <FileSpreadsheet size={28} color="white" />
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', letterSpacing: 1, marginBottom: 2 }}>DAILY REPORT</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: 'white' }}>일일 판매일보 다운로드</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>판매 · 매입 내역을 엑셀로 저장</div>
        </div>
      </div>

      {/* 날짜 선택 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.12)', borderRadius: 12, padding: '10px 16px' }}>
        <button onClick={prevDay} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', padding: 2 }}>
          <ChevronLeft size={18} />
        </button>
        <input
          type="date"
          value={reportDate}
          onChange={e => setReportDate(e.target.value)}
          style={{ background: 'transparent', border: 'none', color: 'white', fontSize: 15, fontWeight: 700, outline: 'none', cursor: 'pointer', textAlign: 'center' }}
        />
        <button onClick={nextDay} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', padding: 2 }}>
          <ChevronRight size={18} />
        </button>
      </div>

      {/* 요약 수치 */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 3 }}>판매 건수</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: 'white' }}>{dayDeliveries.length}<span style={{ fontSize: 12, fontWeight: 400, marginLeft: 2 }}>건</span></div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 3 }}>판매 금액</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: '#93c5fd' }}>
            {totalSalesAmt >= 10000
              ? `${(totalSalesAmt / 10000).toFixed(1)}만`
              : totalSalesAmt.toLocaleString()}
            <span style={{ fontSize: 12, fontWeight: 400, marginLeft: 2 }}>원</span>
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 3 }}>매입 건수</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: 'white' }}>{dayCollections.length}<span style={{ fontSize: 12, fontWeight: 400, marginLeft: 2 }}>건</span></div>
        </div>
      </div>

      {/* 다운로드 버튼 */}
      <button
        onClick={downloadExcel}
        disabled={dayDeliveries.length === 0 && dayCollections.length === 0}
        style={{
          display: 'flex', alignItems: 'center', gap: 9,
          padding: '13px 22px',
          background: (dayDeliveries.length > 0 || dayCollections.length > 0) ? 'white' : 'rgba(255,255,255,0.2)',
          color: (dayDeliveries.length > 0 || dayCollections.length > 0) ? '#185FA5' : 'rgba(255,255,255,0.5)',
          border: 'none', borderRadius: 12,
          fontSize: 14, fontWeight: 800,
          cursor: (dayDeliveries.length > 0 || dayCollections.length > 0) ? 'pointer' : 'not-allowed',
          whiteSpace: 'nowrap',
          boxShadow: (dayDeliveries.length > 0 || dayCollections.length > 0) ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
          transition: 'all 0.2s',
        }}
      >
        <Download size={18} />
        {(dayDeliveries.length === 0 && dayCollections.length === 0) ? '해당일 내역 없음' : '엑셀 다운로드'}
      </button>
    </div>
  );
};

export default DailySalesReport;
